import { LOWER_UUID_PATTERN } from "./kernel-internal.js";
import { REFRESH_TOKEN_MAX_LENGTH } from "../session-transport.js";
import { isoTimestampSchema } from "./kernel-wire-internal.js";
import { CP_STEP_UP_REDEEM_REFUSALS } from "./step-up.js";
import { z } from "zod";
//#region src/kernel/active-role.ts
/**
* The active site role of a CP session (plan 2026-10-22-cp-active-role, D1 to D8, D11, D12, D14,
* K1 to K7, R-001 to R-004, R-007, R-032).
*
* A person holds site roles. A CP session acts as one of them, the active role. The access token
* carries that role alone in `siteRoles`, and in `sitePermissions` the permissions of that role
* plus the grant-derived permissions (`ControlPlaneJwtPayload`).
* This module holds every shape and rule that two programs must agree on:
*
* 1. the switch route `POST /v1/auth/session/active-role`: its body, its refusal bodies, and the
*    step-up operation that a `role-elevation` request binds (`CP_STEP_UP_OPERATIONS`);
* 2. the rules of the switch: the default role (K1, K2), the elevation test (D5, R-003), the
*    elevation that a switch down keeps (R-007), and the settle of a session at each mint;
* 3. the active role of a request, which the audit writers record in `details.activeRole` (D7);
* 4. the `details` of the audit action `operator_role_activated` (D8, R-002);
* 5. the three refusals of a site-role revoke (D11, D12, D14);
* 6. the `aliasAtMint` claim of a delegated token whose mint applied the MCP scope alias (R-032).
*
* Who calls it:
*
* - cp-auth-api (WS-1): the mint, the switch route, `CpJwtAuthGuard`, the revoke guards and
*   `AuditService.record`;
* - the CP console (WS-4): the switch body, the refusal bodies, the default role in the menu;
* - the `zion` CLI (WS-5): the switch body with `refreshToken` under body transport (`zion elevate`);
* - `@zion/cp-audit-client` (the active-role store) reads {@link SITE_ROLE_SLUG_PATTERN};
* - site-admin-api: `McpScopeAliasGuard` reads {@link aliasAppliedAtMint}.
*
* Browser-safe: no Node builtin and no Node global. Every function is pure and never throws. No
* schema uses `.transform()`, so each one stays representable in the JSON Schema that
* `createOpenApiDocument()` writes.
*/
/**
* The switch route of cp-auth-api. The body is {@link ActiveRoleSwitchRequest}. The answer of a
* switch is the new token pair, the body that `POST /v1/auth/refresh` answers
* (`cpSessionResponseSchema` of `session-transport.ts`). The path sits under the refresh cookie
* path `/v1/auth`.
*/
const ACTIVE_ROLE_SWITCH_PATH = "/v1/auth/session/active-role";
/**
* The lifetime of an access token while its session is elevated: 300 s (D4, K5). A mint never sets
* the token past the end of the elevation. So the elevated permissions end within 300 s of the
* expiry, of a sign-out and of a revoke. The normal token keeps its 900 s.
*/
const ELEVATED_ACCESS_TOKEN_TTL_SECONDS = 300;
/**
* The elevation length of a CP target before an administrator changes it: 28,800 s, 8 hours (P-d,
* K7). cp-auth-api keeps the live value in a CP setting and seeds the setting with this value.
*/
const ELEVATION_LIFETIME_DEFAULT_SECONDS = 28800;
/**
* The site role that holds the platform administration permissions. The revoke guards D11 and D12
* name it, and the console hides the remove action of the own row of this role (D12, K11).
*/
const PLATFORM_ADMIN_ROLE = "platform_admin";
/**
* The grammar of a site role slug: a lower-case letter, then at most 63 lower-case letters, digits,
* `_` or `-`. It is the grammar of `ROLE_SLUG_PATTERN` in cp-auth-api, which every stored slug
* holds (migration `0016_assert_no_wildcard_role_slugs.sql`). A `:` or a `*` never matches, so a
* slug can never read as a permission.
*/
const SITE_ROLE_SLUG_PATTERN = /^[a-z][a-z0-9_-]{0,63}$/;
/** The key of `details` in an audit row that records the active role of the request (D7). */
const ACTIVE_ROLE_DETAIL_KEY = "activeRole";
/**
* The claim a delegated mint writes when it applied the MCP scope alias itself (R-032). A guard
* applies the alias only to a delegated token without it.
*/
const ALIAS_AT_MINT_CLAIM = "aliasAtMint";
/** True when a delegated token's mint already applied the MCP scope alias (R-032). */
function aliasAppliedAtMint(payload) {
	return payload.aliasAtMint === true;
}
/**
* Why a switch was refused with 403, the `error` of {@link ActiveRoleSwitchRefusalBody}:
*
* - `role_not_held`: the body names a role that the operator does not hold now;
* - `step_up_invalid`: the switch is an elevation, and its `stepUpRequestId` names no approved
*   `role-elevation` request of this operator for this role. The body carries the `reason`.
*
* An elevation with no `stepUpRequestId` answers 403 with the `StepUpRequiredBody` of `step-up.ts`
* instead, and cp-auth-api creates a new `role-elevation` request for it.
*/
const ACTIVE_ROLE_SWITCH_REFUSALS = ["role_not_held", "step_up_invalid"];
/**
* Why `DELETE /v1/admin/roles/:slug/operators/:id` was refused with 409, the `error` of
* {@link SiteRoleRevokeRefusalBody}:
*
* - `last_platform_admin`: the revoke leaves no holder of {@link PLATFORM_ADMIN_ROLE} (D11);
* - `self_demotion`: the actor revokes their own {@link PLATFORM_ADMIN_ROLE}, also when another
*   holder exists (D12, K11);
* - `system_operator`: the target is the seeded system operator
*   `00000000-0000-0000-0000-000000000001`, of any site role (D14).
*/
const SITE_ROLE_REVOKE_REFUSALS = [
	"last_platform_admin",
	"self_demotion",
	"system_operator"
];
const lowerUuidSchema = z.string().length(36).regex(LOWER_UUID_PATTERN, "a lower-case UUID");
/** The strict parse of one site role slug: 1 to 64 characters of {@link SITE_ROLE_SLUG_PATTERN}. */
const siteRoleSlugSchema = z.string().min(1).max(64).regex(SITE_ROLE_SLUG_PATTERN, "a site role slug");
/**
* The strict parse of {@link ActiveRoleSwitchRequest}. It refuses a `null` role, a role outside
* {@link SITE_ROLE_SLUG_PATTERN}, a `stepUpRequestId` that is not a lower-case UUID, an empty or
* over-long `refreshToken`, and any other key. Under `ZionStrictValidationPipe` a refusal answers 400 before the route runs.
*/
const activeRoleSwitchRequestSchema = z.object({
	role: siteRoleSlugSchema,
	stepUpRequestId: lowerUuidSchema.optional(),
	refreshToken: z.string().min(1).max(REFRESH_TOKEN_MAX_LENGTH).optional()
}).strict();
/**
* The strict parse of {@link ActiveRoleSwitchRefusalBody}. It refuses a `step_up_invalid` body with
* no `reason`, a `role_not_held` body with a `reason`, and any key the filter does not write.
*/
const activeRoleSwitchRefusalBodySchema = z.object({
	statusCode: z.literal(403),
	error: z.enum(ACTIVE_ROLE_SWITCH_REFUSALS),
	message: z.string().min(1).max(500),
	reason: z.enum(CP_STEP_UP_REDEEM_REFUSALS).optional(),
	timestamp: z.string().max(64).optional(),
	path: z.string().max(2048).optional()
}).strict().superRefine((body, ctx) => {
	if (body.reason !== void 0 !== (body.error === "step_up_invalid")) ctx.addIssue({
		code: "custom",
		path: ["reason"],
		message: "a step_up_invalid body names its reason, and only it"
	});
});
/** The strict parse of {@link SiteRoleRevokeRefusalBody}. */
const siteRoleRevokeRefusalBodySchema = z.object({
	statusCode: z.literal(409),
	error: z.enum(SITE_ROLE_REVOKE_REFUSALS),
	message: z.string().min(1).max(500),
	timestamp: z.string().max(64).optional(),
	path: z.string().max(2048).optional()
}).strict();
/**
* The strict parse of {@link OperatorRoleActivatedDetails}. It refuses a `stepUpRequestId` beside
* a `null` `elevatedUntil`, because a consumed step-up always starts an elevation.
*/
const operatorRoleActivatedDetailsSchema = z.object({
	fromRole: siteRoleSlugSchema.nullable(),
	toRole: siteRoleSlugSchema,
	elevatedUntil: isoTimestampSchema.nullable(),
	stepUpRequestId: lowerUuidSchema.nullable(),
	activeRole: siteRoleSlugSchema.nullable().optional()
}).strict().superRefine((details, ctx) => {
	if (details.stepUpRequestId !== null && details.elevatedUntil === null) ctx.addIssue({
		code: "custom",
		path: ["elevatedUntil"],
		message: "a consumed step-up starts an elevation"
	});
});
function permissionSetOf(catalog, role) {
	return new Set(Object.hasOwn(catalog, role) ? catalog[role] : []);
}
/** True when every permission of `role` is a permission of `of`. */
function isSubsetRole(catalog, role, of) {
	const outer = permissionSetOf(catalog, of);
	for (const permission of permissionSetOf(catalog, role)) if (!outer.has(permission)) return false;
	return true;
}
/**
* The default role of a person (K1, K2, R-004): the held role with the fewest distinct permissions.
* A tie goes to the slug that sorts first by UTF-16 code unit. `null` when `held` is empty. A
* person who holds `platform_admin` alone gets `platform_admin`. A repeated slug counts once.
*
* Examples on the 2026-10-22 catalog: `['fleet_operator', 'app_developer']` gives `app_developer`
* (1 permission); `['app_developer', 'platform_admin']` gives `app_developer`.
*
* A role that holds a wildcard permission (`*`, or a string that ends in `:*`) ranks above every
* role with no wildcard, whatever its count, so it is the default only when every held role has a
* wildcard (least privilege). Among wildcard roles the widest wildcard decides first: `*` ranks
* above `<domain>:*`, and `cp:*` above `cp:app:*` (fewer segments before `:*` is wider). Then the
* count and then the slug decide. Pure; never throws.
*/
function defaultSiteRole(held, catalog) {
	let best = null;
	let bestDepth = 0;
	let bestSize = 0;
	for (const role of held) {
		const permissions = permissionSetOf(catalog, role);
		const depth = wildcardDepth(permissions);
		const size = permissions.size;
		if (best === null || (depth !== bestDepth ? depth > bestDepth : size < bestSize || size === bestSize && role < best)) {
			best = role;
			bestDepth = depth;
			bestSize = size;
		}
	}
	return best;
}
/**
* The reach of the widest wildcard of a role, where a smaller number is wider: `0` for `*`, the
* count of the segments before `:*` for a prefix wildcard (`cp:*` is `1`, `cp:app:*` is `2`), and
* `Infinity` for a role with no wildcard.
*/
function wildcardDepth(permissions) {
	let depth = Number.POSITIVE_INFINITY;
	for (const permission of permissions) {
		if (permission === "*") return 0;
		if (permission.endsWith(":*")) depth = Math.min(depth, permission.split(":").length - 1);
	}
	return depth;
}
/**
* The verdict of a switch of the session in `state` to `to`. The caller settles the session first
* ({@link settleActiveRole}), so `state.activeRole` and `state.elevatedUntil` are the settled values.
*
* The rules, in order:
*
* 1. `to` is not in `held`: `refused`.
* 2. `to` is the active role and the session is elevated: `elevation` with `renew: true` (K4).
* 3. `to` is the active role: `unchanged`.
* 4. `to` is the default role ({@link defaultSiteRole}): `switch` with `keepsElevation: false`.
*    A return to the default never needs a passkey, also when the two roles are incomparable
*    (R-003).
* 5. `to` holds a permission that the active role does not hold, or no role is active: `elevation`
*    with `renew: false` (D5).
* 6. Otherwise `to` is a switch down: `switch`. It keeps the elevation when the session is elevated
*    and `to` holds a permission that the default role does not hold (R-007). A switch from
*    `platform_admin` to `fleet_operator` over an `app_developer` default so keeps its end.
*
* "Elevated" means `elevatedUntil` is a number greater than `nowSeconds`. Pure; never throws.
*/
function classifyActiveRoleSwitch(state, to) {
	if (!state.held.includes(to)) return {
		kind: "refused",
		reason: "role_not_held"
	};
	const elevated = state.elevatedUntil !== null && state.elevatedUntil > state.nowSeconds;
	if (to === state.activeRole) return elevated ? {
		kind: "elevation",
		renew: true
	} : { kind: "unchanged" };
	const rest = defaultSiteRole(state.held, state.catalog);
	if (rest === null || to === rest) return {
		kind: "switch",
		keepsElevation: false
	};
	if (state.activeRole === null || !isSubsetRole(state.catalog, to, state.activeRole)) return {
		kind: "elevation",
		renew: false
	};
	return {
		kind: "switch",
		keepsElevation: elevated && !isSubsetRole(state.catalog, to, rest)
	};
}
/**
* The state that a mint writes, from the stored state of a session and the held roles of now. The
* mint of every access token and every refresh calls it, and the session stores the answer.
*
* The rules, in order, where `rest` is {@link defaultSiteRole} of `held`:
*
* 1. An elevation that ended (`elevatedUntil <= nowSeconds`): `rest`, not elevated.
* 2. An active role that the operator no longer holds, or no active role: `rest`, not elevated
*    (D13). A person who holds no site role so settles to `null`.
* 3. The active role is `rest`: `rest`, not elevated.
* 4. A session that is not elevated and acts as a role that holds a permission `rest` does not hold:
*    `rest`. A role above the default needs an elevation (D5).
* 5. An elevated session whose active role holds no permission outside `rest`: the same role, not
*    elevated, since it needs no elevation.
* 6. Otherwise the stored state, unchanged.
*
* Pure; never throws.
*/
function settleActiveRole(state) {
	const restRole = defaultSiteRole(state.held, state.catalog);
	const atRest = {
		activeRole: restRole,
		elevatedUntil: null,
		restRole
	};
	const { activeRole, elevatedUntil } = state;
	if (elevatedUntil !== null && elevatedUntil <= state.nowSeconds) return atRest;
	if (activeRole === null || !state.held.includes(activeRole) || activeRole === restRole || restRole === null) return atRest;
	const aboveRest = !isSubsetRole(state.catalog, activeRole, restRole);
	if (elevatedUntil === null) return aboveRest ? atRest : {
		activeRole,
		elevatedUntil: null,
		restRole
	};
	return {
		activeRole,
		elevatedUntil: aboveRest ? elevatedUntil : null,
		restRole
	};
}
/**
* The lifetime in seconds of an access token minted at `nowSeconds` (D4, K5). Not elevated:
* `normalTtlSeconds`. Elevated: {@link ELEVATED_ACCESS_TOKEN_TTL_SECONDS}, and never past the end of
* the elevation, with a floor of 1 s. Pure; never throws.
*/
function activeRoleTokenTtlSeconds(elevatedUntil, nowSeconds, normalTtlSeconds) {
	if (elevatedUntil === null) return normalTtlSeconds;
	return Math.max(1, Math.min(300, Math.floor(elevatedUntil - nowSeconds)));
}
/**
* The step-up operation that a `role-elevation` request binds: `POST`
* {@link ACTIVE_ROLE_SWITCH_PATH} with the body `{ role }` alone, no org and no query. The
* `stepUpRequestId` of the switch body stays out of the digest, because the client sends it only on
* the repeat. cp-auth-api passes the answer to `stepUpOperationDigest` when it creates the request
* and again when it consumes it. Pure; never throws.
*/
function roleElevationOperation(role) {
	return {
		orgId: null,
		method: "POST",
		path: ACTIVE_ROLE_SWITCH_PATH,
		query: {},
		body: { role }
	};
}
/**
* The active role of a request, from its verified CP token (the defaults row "the active role of a
* request"). The guard of each CP service passes the answer to the active-role store of
* `@zion/cp-audit-client`.
*
* - A service token (`sub` starts with `svc:`): `null`. A service has no active role.
* - A token with no `heldSiteRoles` claim: `null`. It predates the active role, and its
*   `siteRoles` is the union of the held roles, so no entry of it is the active role.
* - A token whose `siteRoles` holds exactly one slug of {@link SITE_ROLE_SLUG_PATTERN}: that slug.
* - Any other token: `null`.
*
* Pure; never throws.
*/
function activeRoleOfToken(payload) {
	const role = payload.siteRoles[0];
	if (payload.sub.startsWith("svc:") || payload.heldSiteRoles === void 0 || payload.siteRoles.length !== 1) return null;
	return typeof role === "string" && SITE_ROLE_SLUG_PATTERN.test(role) ? role : null;
}
//#endregion
export { ACTIVE_ROLE_DETAIL_KEY, ACTIVE_ROLE_SWITCH_PATH, ACTIVE_ROLE_SWITCH_REFUSALS, ALIAS_AT_MINT_CLAIM, ELEVATED_ACCESS_TOKEN_TTL_SECONDS, ELEVATION_LIFETIME_DEFAULT_SECONDS, PLATFORM_ADMIN_ROLE, SITE_ROLE_REVOKE_REFUSALS, SITE_ROLE_SLUG_PATTERN, activeRoleOfToken, activeRoleSwitchRefusalBodySchema, activeRoleSwitchRequestSchema, activeRoleTokenTtlSeconds, aliasAppliedAtMint, classifyActiveRoleSwitch, defaultSiteRole, operatorRoleActivatedDetailsSchema, roleElevationOperation, settleActiveRole, siteRoleRevokeRefusalBodySchema, siteRoleSlugSchema };

//# sourceMappingURL=active-role.js.map